import { supabase } from './supabase';

export interface ColorScheme {
  name: string;
  primary: string;
  secondary: string;
  text: string;
  background: string;
  accent?: string;
}

export interface FontSettings {
  heading: string;
  body: string;
  size: {
    heading: number;
    subheading: number;
    body: number;
  };
  lineHeight?: {
    heading?: number;
    body?: number;
  };
}

export interface ResumeTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  layout_type: string;
  template_data: any;
  color_schemes: ColorScheme[];
  font_settings: FontSettings;
  features: string[];
  ats_score: number;
  popularity_score: number;
  industry_tags: string[];
  is_premium: boolean;
  is_active: boolean;
  preview_url: string | null;
  created_at: string;
}

export interface ResumeMetadata {
  resume_id: string;
  custom_name?: string;
  is_favorite: boolean;
  color_scheme: string;
  font_scheme: string;
  section_order: string[];
  hidden_sections: string[];
  formatting_preferences: any;
}

export interface ResumeExport {
  id: string;
  resume_id: string;
  export_format: 'pdf' | 'docx' | 'txt';
  file_url?: string;
  file_size: number;
  created_at: string;
}

export interface ResumeShare {
  id: string;
  resume_id: string;
  share_type: 'email' | 'link';
  recipient_email?: string;
  share_token: string;
  message?: string;
  expires_at?: string;
  view_count: number;
  last_viewed_at?: string;
  created_at: string;
}

class ResumeTemplateService {
  async getTemplates(options?: {
    category?: string;
    industryTag?: string;
    searchQuery?: string;
  }): Promise<ResumeTemplate[]> {
    let query = supabase
      .from('resume_templates')
      .select('*')
      .eq('is_active', true);

    if (options?.category) {
      query = query.eq('category', options.category);
    }

    if (options?.industryTag) {
      query = query.contains('industry_tags', [options.industryTag]);
    }

    if (options?.searchQuery) {
      query = query.or(`name.ilike.%${options.searchQuery}%,description.ilike.%${options.searchQuery}%`);
    }

    const { data, error } = await query.order('popularity_score', { ascending: false });

    if (error) {
      console.error('Error fetching templates:', error);
      return [];
    }

    return data || [];
  }

  async getTemplate(templateId: string): Promise<ResumeTemplate | null> {
    const { data, error } = await supabase
      .from('resume_templates')
      .select('*')
      .eq('id', templateId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching template:', error);
      return null;
    }

    return data;
  }

  async getFavoriteTemplates(userId: string): Promise<ResumeTemplate[]> {
    const { data, error} = await supabase
      .from('template_favorites')
      .select('template_id, resume_templates(*)')
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching favorite templates:', error);
      return [];
    }

    return data?.map((fav: any) => fav.resume_templates).filter(Boolean) || [];
  }

  async toggleFavoriteTemplate(userId: string, templateId: string): Promise<boolean> {
    const { data: existing } = await supabase
      .from('template_favorites')
      .select('id')
      .eq('user_id', userId)
      .eq('template_id', templateId)
      .maybeSingle();

    if (existing) {
      const { error } = await supabase
        .from('template_favorites')
        .delete()
        .eq('id', existing.id);

      return !error;
    } else {
      const { error } = await supabase
        .from('template_favorites')
        .insert({
          user_id: userId,
          template_id: templateId,
        });

      return !error;
    }
  }

  async isFavoriteTemplate(userId: string, templateId: string): Promise<boolean> {
    const { data } = await supabase
      .from('template_favorites')
      .select('id')
      .eq('user_id', userId)
      .eq('template_id', templateId)
      .maybeSingle();

    return !!data;
  }

  async getResumeMetadata(resumeId: string): Promise<ResumeMetadata | null> {
    const { data, error } = await supabase
      .from('resume_metadata')
      .select('*')
      .eq('resume_id', resumeId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching resume metadata:', error);
      return null;
    }

    return data;
  }

  async upsertResumeMetadata(metadata: Partial<ResumeMetadata> & { resume_id: string }): Promise<boolean> {
    const { error } = await supabase
      .from('resume_metadata')
      .upsert(metadata, { onConflict: 'resume_id' });

    if (error) {
      console.error('Error upserting resume metadata:', error);
      return false;
    }

    return true;
  }

  async updateResumeName(resumeId: string, customName: string): Promise<boolean> {
    return await this.upsertResumeMetadata({
      resume_id: resumeId,
      custom_name: customName,
    });
  }

  async toggleFavoriteResume(resumeId: string): Promise<boolean> {
    const metadata = await this.getResumeMetadata(resumeId);
    const newFavoriteStatus = !metadata?.is_favorite;

    return await this.upsertResumeMetadata({
      resume_id: resumeId,
      is_favorite: newFavoriteStatus,
    });
  }

  async setDefaultResume(userId: string, resumeId: string): Promise<boolean> {
    const { error } = await supabase
      .from('profiles')
      .update({ default_resume_id: resumeId })
      .eq('id', userId);

    if (error) {
      console.error('Error setting default resume:', error);
      return false;
    }

    return true;
  }

  async getDefaultResume(userId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('default_resume_id')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error getting default resume:', error);
      return null;
    }

    return data?.default_resume_id || null;
  }

  async createResumeExport(
    userId: string,
    resumeId: string,
    format: 'pdf' | 'docx' | 'txt',
    fileUrl?: string,
    fileSize?: number
  ): Promise<ResumeExport | null> {
    const { data, error } = await supabase
      .from('resume_exports')
      .insert({
        user_id: userId,
        resume_id: resumeId,
        export_format: format,
        file_url: fileUrl,
        file_size: fileSize || 0,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating resume export:', error);
      return null;
    }

    return data;
  }

  async getResumeExports(resumeId: string): Promise<ResumeExport[]> {
    const { data, error } = await supabase
      .from('resume_exports')
      .select('*')
      .eq('resume_id', resumeId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching resume exports:', error);
      return [];
    }

    return data || [];
  }

  async shareResumeViaEmail(
    userId: string,
    resumeId: string,
    recipientEmail: string,
    message?: string
  ): Promise<ResumeShare | null> {
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 30);

    const { data, error } = await supabase
      .from('resume_shares')
      .insert({
        user_id: userId,
        resume_id: resumeId,
        share_type: 'email',
        recipient_email: recipientEmail,
        message: message,
        expires_at: expiresAt.toISOString(),
      })
      .select()
      .single();

    if (error) {
      console.error('Error sharing resume via email:', error);
      return null;
    }

    return data;
  }

  async createShareLink(
    userId: string,
    resumeId: string,
    expiresInDays: number = 30
  ): Promise<ResumeShare | null> {
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + expiresInDays);

    const { data, error } = await supabase
      .from('resume_shares')
      .insert({
        user_id: userId,
        resume_id: resumeId,
        share_type: 'link',
        expires_at: expiresAt.toISOString(),
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating share link:', error);
      return null;
    }

    return data;
  }

  async getResumeByShareToken(shareToken: string): Promise<any | null> {
    const { data: share, error: shareError } = await supabase
      .from('resume_shares')
      .select('*, resumes(*)')
      .eq('share_token', shareToken)
      .maybeSingle();

    if (shareError || !share) {
      console.error('Error fetching shared resume:', shareError);
      return null;
    }

    if (share.expires_at && new Date(share.expires_at) < new Date()) {
      return null;
    }

    await supabase
      .from('resume_shares')
      .update({
        view_count: share.view_count + 1,
        last_viewed_at: new Date().toISOString(),
      })
      .eq('id', share.id);

    return share.resumes;
  }

  async getResumeShares(resumeId: string): Promise<ResumeShare[]> {
    const { data, error } = await supabase
      .from('resume_shares')
      .select('*')
      .eq('resume_id', resumeId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching resume shares:', error);
      return [];
    }

    return data || [];
  }

  async updateTemplatePopularity(templateId: string): Promise<void> {
    const { data: template } = await supabase
      .from('resume_templates')
      .select('popularity_score')
      .eq('id', templateId)
      .maybeSingle();

    if (template) {
      await supabase
        .from('resume_templates')
        .update({
          popularity_score: (template.popularity_score || 0) + 1,
        })
        .eq('id', templateId);
    }
  }

  getShareableLink(shareToken: string): string {
    return `${window.location.origin}/shared-resume/${shareToken}`;
  }
}

export const resumeTemplateService = new ResumeTemplateService();
